<!-- src/components/Common/GlobalConfirmModal.vue -->

<template>
  <div v-if="visible" class="fixed inset-0 z-[11000] flex items-center justify-center bg-black/40">
    <div class="w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-xl">
      <h2 class="mb-3 text-lg font-semibold text-gray-800">{{ title }}</h2>
      <p class="mb-6 whitespace-pre-wrap text-sm text-gray-600">{{ message }}</p>
      <div class="flex justify-center gap-3">
        <button
          class="rounded bg-gray-200 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="working"
          @click="cancel"
        >
          Cancel
        </button>
        <!-- An async onConfirm holds the modal open with this button busy
             until it settles (button-guard sweep, 2026-08-16). Sync callers
             (incl. useConfirm's resolve) close instantly, exactly as before.
             BusyButton keeps the size constant across the swap — no wobble. -->
        <BusyButton
          class="rounded bg-red-500 px-4 py-2 text-sm text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-red-300"
          :busy="working"
          busy-label="Working…"
          @click="confirm"
        >
          {{ confirmText }}
        </BusyButton>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { eventBus } from '@/utils/eventBus'
import BusyButton from '@/components/Common/BusyButton.vue'

export default {
  components: { BusyButton },
  setup() {
    const visible = ref(false)
    const title = ref('Confirm')
    const message = ref('')
    const confirmText = ref('Confirm')
    const working = ref(false)
    let confirmCallback = null
    let cancelCallback = null

    // Accepts optional onCancel so callers can distinguish "Confirm" from
    // "Cancel" (or escape/backdrop dismiss, which we map to onCancel too).
    const open = ({ title: t, text, confirmButtonText, onConfirm, onCancel }) => {
      title.value = t || 'Confirm'
      message.value = text || 'Are you sure?'
      confirmText.value = confirmButtonText || 'Confirm'
      confirmCallback = onConfirm || null
      cancelCallback = onCancel || null
      working.value = false
      visible.value = true
    }

    const close = () => {
      visible.value = false
      confirmCallback = null
      cancelCallback = null
    }

    const confirm = () => {
      if (working.value) return
      const cb = confirmCallback
      if (!cb) {
        close()
        return
      }
      const result = cb()
      if (result && typeof result.then === 'function') {
        // Async onConfirm: stay open + busy until it settles. Errors are the
        // caller's to surface; the modal's job is only to not lie about
        // being done.
        working.value = true
        result.finally(() => {
          working.value = false
          close()
        })
      } else {
        close()
      }
    }

    const cancel = () => {
      if (working.value) return
      const cb = cancelCallback
      close()
      if (cb) cb()
    }

    const onKeydown = e => {
      if (!visible.value) return
      if (e.key === 'Escape') cancel()
    }

    onMounted(() => {
      eventBus.on('confirm', open)
      window.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount(() => {
      eventBus.off?.('confirm', open)
      window.removeEventListener('keydown', onKeydown)
    })

    return { visible, title, message, confirmText, working, confirm, cancel }
  },
}
</script>
